import React, { useState, useEffect, useMemo } from "react";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { ChevronDown } from "lucide-react";

const SECTION_META = {
  subjective: { label: "S", title: "Subjective", bg: "#E6F1FB", fg: "#0C447C" },
  objective: { label: "O", title: "Objective", bg: "#E1F5EE", fg: "#04342C" },
  assessment: { label: "A", title: "Assessment", bg: "#FAEEDA", fg: "#412402" },
  plan: { label: "P", title: "Plan", bg: "#EEEDFE", fg: "#26215C" },
  notes: { label: "N", title: "Notes", bg: "#F3F4F6", fg: "#374151" },
};

// ─── Text cleanup helpers ───────────────────────────────────────────────
const splitLoose = (str) =>
  (str || "")
    .split(/\n|,/)
    .map((s) => s.trim())
    .filter(Boolean);

// const isMeaningful = (val) =>
//   val !== undefined && val !== null && val !== "" &&
//   !(Array.isArray(val) && val.length === 0) &&
//   val !== "No" && val !== "Normal";

const isMeaningful = (val) => {
  if (val === undefined || val === null || val === "") return false;
  if (Array.isArray(val)) {
    const filtered = val.filter((v) => v && v !== "None" && v !== "Normal" && v !== "Not present");
    return filtered.length > 0;
  }
  return val !== "No" && val !== "Normal";
};

const formatValue = (val) => (Array.isArray(val) ? val.join(", ") : val);


// ─── Small labeled-field primitives ─────────────────────────────────────

const FieldRow = ({ label, value }) => {
  if (!isMeaningful(value)) return null;
  return (
    <div className="flex gap-1.5 text-sm">
      <span className="text-gray-500 shrink-0">{label}:</span>
      <span className="text-gray-800">{value}</span>
    </div>
  );
};

const Pill = ({ children, tone = "gray" }) => {
  const tones = {
    gray: "bg-gray-50 text-gray-700 border-gray-200",
    teal: "bg-teal-50 text-teal-700 border-teal-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`text-xs px-2 py-1 rounded-full border ${tones[tone]}`}>
      {children}
    </span>
  );
};

const EmptyNote = ({ children }) => (
  <p className="text-sm text-gray-400 italic">{children}</p>
);

// ─── Subjective ───────────────────────────────────────────────────────

const SubjectiveBody = ({ subjective = {} }) => {
  const baseFields = [
    ["Diet", subjective.diet],
    ["Activity level", subjective.activityLevel],
    ["Water intake", subjective.waterIntake],
    ["Defecation", subjective.defecation],
    ["Vaccination status", subjective.vaccinationStatus],
    ["Problem duration", subjective.problemDuration],
  ].filter(([, v]) => isMeaningful(v));

  const vomiting = subjective.vomiting;
  const hasVomiting = vomiting?.present === "Yes";

  const structuredEntries = (subjective.structuredEntries || []).filter((e) => isMeaningful(e.answer));
  const conditions = subjective.medicalConditions || [];

  const hasAnything = baseFields.length || hasVomiting || structuredEntries.length || conditions.length;
  if (!hasAnything) return <EmptyNote>No subjective findings recorded for this visit.</EmptyNote>;

  return (
    <div className="space-y-2">
      {baseFields.map(([label, value]) => (
        <FieldRow key={label} label={label} value={value} />
      ))}

      {hasVomiting && (
        <div className="text-sm">
          <span className="text-gray-500">Vomiting: </span>
          <span className="text-gray-800">
            {[vomiting.frequency, vomiting.colour, vomiting.blood === "Yes" ? "blood present" : null]
              .filter(Boolean)
              .join(", ") || "present"}
          </span>
        </div>
      )}

      {conditions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {conditions.map((c, i) => (
            <Pill key={i} tone="amber">{c}</Pill>
          ))}
        </div>
      )}

      {structuredEntries.length > 0 && (
        <div className="pt-1 space-y-1">
          {structuredEntries.map((e) => (
            <FieldRow key={e._id} label={e.question} value={e.answer} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Objective ────────────────────────────────────────────────────────

const VITAL_LABELS = {
  weight: { label: "Weight", unit: "kg" },
  temp: { label: "Temp", unit: "°F" },
  hr: { label: "HR", unit: "bpm" },
  rr: { label: "RR", unit: "/min" },
  bcs: { label: "BCS", unit: "" },
  painScore: { label: "Pain score", unit: "" },
};

const VitalsTable = ({ vitals = [] }) => {
  if (vitals.length === 0) return null;

  return (
    <div>
      <span className="text-xs font-medium text-gray-500">
        Vitals {vitals.length > 1 ? `(${vitals.length} recordings)` : ""}
      </span>
      <div className="mt-1 space-y-2">
        {vitals.map((v, idx) => (
          <div key={v._id || idx} className="border border-gray-100 rounded-lg px-2.5 py-1.5">
            {(v.date || v.time) && (
              <p className="text-[11px] text-gray-400 mb-1">
                {[v.date, v.time].filter(Boolean).join(", ")}
              </p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {Object.entries(VITAL_LABELS).map(([key, { label, unit }]) =>
                isMeaningful(v[key]) ? (
                  <span key={key} className="text-sm">
                    <span className="text-gray-500">{label}:</span>{" "}
                    <span className="text-gray-800 font-medium">{v[key]}{unit}</span>
                  </span>
                ) : null
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// const EXAM_FIELD_LABELS = {
//   hydration: "Hydration",
//   depressed: "Depressed",
//   mentation: "Mentation",
// };

// const ExamFindings = ({ examFindings = {} }) => {
//   const rows = Object.values(examFindings)
//     .flatMap((section) => Object.entries(section || {}))
//     .filter(([, val]) => isMeaningful(val));

//   if (rows.length === 0) return null;

//   return (
//     <div>
//       <span className="text-xs font-medium text-gray-500">Exam findings</span>
//       <div className="mt-1 space-y-1">
//         {rows.map(([key, val]) => (
//           <FieldRow key={key} label={EXAM_FIELD_LABELS[key] || key} value={val} />
//         ))}
//       </div>
//     </div>
//   );
// };

 const EXAM_SECTIONS = {
  general: { title: "General", fields: { hydration: "Hydration", mentation: "Mentation" } },
  eyes: { title: "Eyes", fields: { leftEye: "Left eye", rightEye: "Right eye", discharge: "Discharge", redness: "Redness" } },
  ears: { title: "Ears", fields: { leftEar: "Left ear", rightEar: "Right ear", discharge: "Discharge", odour: "Odour" } },
  oral: { title: "Oral", fields: { mm: "Mucous membranes", dentalGrade: "Dental grade", crt: "CRT" } },
  skin: { title: "Skin", fields: { coatCondition: "Coat condition", alopecia: "Alopecia", skinFindings: "Findings", ticks: "Ticks", lumps: "Lumps", wounds: "Wounds" } },
  cardiovascular: { title: "Cardiovascular", fields: { hr: "HR", rhythm: "Rhythm", murmur: "Murmur" } },
  respiratory: { title: "Respiratory", fields: { rr: "RR", lungSounds: "Lung sounds" } },
  gi: { title: "GI", fields: { appetite: "Appetite", abdominal: "Abdominal" } },
  musculoskeletal: { title: "Musculoskeletal", fields: { gait: "Gait", lameness: "Lameness", affectedLimb: "Affected limb" } },
  neurological: { title: "Neurological", fields: { mentation: "Mentation", reflexes: "Reflexes", findings: "Findings" } },
};

const ExamFindings = ({ examFindings = {} }) => {
  const sections = Object.entries(EXAM_SECTIONS)
    .map(([key, { title, fields }]) => {
      const data = examFindings[key] || {};
      const rows = Object.entries(fields)
        .filter(([fieldKey]) => isMeaningful(data[fieldKey]))
        .map(([fieldKey, label]) => ({ label, value: formatValue(data[fieldKey]) }));
      return { title, rows };
    })
    .filter((section) => section.rows.length > 0);

  if (sections.length === 0) return null;

  return (
    <div>
      <span className="text-xs font-medium text-gray-500">Exam findings</span>
      <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {sections.map((section) => (
          <div key={section.title} className="border border-gray-100 rounded-lg px-2.5 py-1.5">
            <p className="text-[11px] font-medium text-gray-500 mb-1">{section.title}</p>
            <div className="space-y-0.5">
              {section.rows.map((row) => (
                <FieldRow key={row.label} label={row.label} value={row.value} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ObjectiveBody = ({ objective = {} }) => {
  const vitals = objective.vitals || [];
  const hasExamFindings = Object.values(objective.examFindings || {}).some((section) =>
    Object.values(section || {}).some(isMeaningful)
  );
  const labTests = objective.labTests || [];
  const hasNotes = isMeaningful(objective.examinationNotes);

  const hasAnything = vitals.length || hasExamFindings || labTests.length || hasNotes;
  if (!hasAnything) return <EmptyNote>No objective findings recorded for this visit.</EmptyNote>;

  return (
    <div className="space-y-3">
      <VitalsTable vitals={vitals} />
      <ExamFindings examFindings={objective.examFindings} />
      {hasNotes && <FieldRow label="Examination notes" value={objective.examinationNotes} />}

      {labTests.length > 0 && (
        <div>
          <span className="text-xs font-medium text-gray-500">Lab tests ordered</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {labTests.map((t) => (
              <LabTestChip key={t.id} test={t} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Assessment ───────────────────────────────────────────────────────

const AssessmentBody = ({ assessment = {} }) => {
  const diagnosis = splitLoose(assessment.diagnosis);
  const differentials = (assessment.differentialsList || []).flatMap(splitLoose);
  const hasNotes = isMeaningful(assessment.clinicalNotes);
  const problemList = assessment.problemList || [];

  const hasAnything =
    diagnosis.length || differentials.length || hasNotes || problemList.length ||
    isMeaningful(assessment.severity) || isMeaningful(assessment.prognosis);

  if (!hasAnything) return <EmptyNote>No assessment recorded for this visit.</EmptyNote>;

  return (
    <div className="space-y-2">
      {diagnosis.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {diagnosis.map((d, i) => (
            <Pill key={i} tone="red">{d}</Pill>
          ))}
          {isMeaningful(assessment.severity) && <Pill tone="amber">{assessment.severity}</Pill>}
        </div>
      )}

      {hasNotes && <FieldRow label="Clinical notes" value={assessment.clinicalNotes} />}

      {differentials.length > 0 && (
        <div>
          <span className="text-xs font-medium text-gray-500">Differentials</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {differentials.map((d, i) => (
              <Pill key={i}>{d}</Pill>
            ))}
          </div>
        </div>
      )}

      {problemList.length > 0 && <FieldRow label="Problem list" value={problemList.join(", ")} />}
      <FieldRow label="Prognosis" value={assessment.prognosis} />
    </div>
  );
};

// ─── Plan ─────────────────────────────────────────────────────────────

const doseSchedule = (m) => {
  const parts = [m.morning, m.afternoon, m.night].map((n) => n || 0);
  if (parts.every((n) => n === 0)) return null;
  return parts.join("-");
};

const MedicationsList = ({ medications = [] }) => {
  if (medications.length === 0) return null;
  return (
    <div>
      <span className="text-xs font-medium text-gray-500">Medications prescribed</span>
      <div className="mt-1 space-y-1">
        {medications.map((m, i) => {
          const schedule = doseSchedule(m);
          const detail = [
            schedule && `${schedule} ${m.unit || ""}`.trim(),
            m.foodTiming,
            m.duration ? `${m.duration} day${m.duration > 1 ? "s" : ""}` : null,
          ].filter(Boolean).join(" · ");
          return (
            <div key={i} className="text-sm">
              <span className="text-gray-800 font-medium">{m.drugName}</span>
              {detail && <span className="text-gray-500"> — {detail}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};


 const ProceduresList = ({ procedures = [] }) => {
  if (procedures.length === 0) return null;
  return (
    <div>
      <span className="text-xs font-medium text-gray-500">Procedures</span>
      <div className="mt-1 space-y-1">
        {procedures.map((p) => (
          <div key={p._id} className="text-sm">
            <span className="text-gray-800 font-medium">{p.name}{p.quantity > 1 ? ` ×${p.quantity}` : ""}</span>
            <span className="text-gray-500"> — ₹{(p.total ?? p.price)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PlanBody = ({ plan = {} }) => {
  const proceduresList = plan.proceduresList || [];
  const medications = plan.prescription?.medications || [];
  const recheckDate = plan.followUpData?.recheckDate;
  const nextVaxDate = plan.followUpData?.nextVaccinationDate;
  const followUpNotes = plan.followUpData?.notes;
  const quickRecs = plan.recommendations?.quickRecommendations || [];
  const recNotes = plan.recommendations?.notes;
  const rxNotes = plan.prescription?.notes;
  const prescriptionNo = plan.prescription?.prescriptionNo;
  const prescriptionUrl = plan.prescription?.prescriptionUrl;
  const treatmentPlanName = plan.appliedTreatmentPlan?.name;
  const additionalTesting = plan.followUpData?.additionalTesting;

  const hasAnything =
    isMeaningful(plan.plan) || isMeaningful(plan.diagnosticsOrdered) ||
    proceduresList.length || medications.length ||
    isMeaningful(recheckDate) || isMeaningful(nextVaxDate) || isMeaningful(followUpNotes) ||
    quickRecs.length || isMeaningful(recNotes) || isMeaningful(rxNotes)||
    isMeaningful(prescriptionNo) || isMeaningful(prescriptionUrl)||
    isMeaningful(treatmentPlanName);

  if (!hasAnything) return <EmptyNote>No treatment plan recorded for this visit.</EmptyNote>;
   
   return (
    <div className="space-y-2.5">
      {isMeaningful(treatmentPlanName) && (
        <div className="flex flex-wrap gap-1.5">
          <Pill tone="violet">{treatmentPlanName}</Pill>
        </div>
      )}

      <FieldRow label="Plan" value={plan.plan} />
      <FieldRow label="Diagnostics ordered" value={plan.diagnosticsOrdered} />

      <ProceduresList procedures={proceduresList} />

      <MedicationsList medications={medications} />
      {isMeaningful(rxNotes) && <FieldRow label="Prescription notes" value={rxNotes} />}

      {(isMeaningful(prescriptionNo) || isMeaningful(prescriptionUrl)) && (
        <div className="text-sm flex items-center gap-1.5">
          <span className="text-gray-500">Prescription:</span>
          {isMeaningful(prescriptionNo) && <span className="text-gray-800">{prescriptionNo}</span>}
          {isMeaningful(prescriptionUrl) && (
            <a href={prescriptionUrl} target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">
              view PDF
            </a>
          )}
        </div>
      )}

      {quickRecs.length > 0 && <FieldRow label="Recommendations" value={quickRecs.join(", ")} />}
      {isMeaningful(recNotes) && <FieldRow label="Recommendation notes" value={recNotes} />}

      {(isMeaningful(recheckDate) || isMeaningful(nextVaxDate) || isMeaningful(followUpNotes) || isMeaningful(additionalTesting)) && (
        <div>
          <span className="text-xs font-medium text-gray-500">Follow-up</span>
          <div className="mt-1 space-y-1">
            <FieldRow label="Recheck date" value={recheckDate} />
            <FieldRow label="Next vaccination" value={nextVaxDate} />
            <FieldRow label="Additional testing" value={additionalTesting} />
            <FieldRow label="Notes" value={followUpNotes} />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Notes ───────────────────────────────────────────────────────────

const NotesBody = ({ notes = {} }) => {
  const generalNotes = notes.generalNotes || notes.notes || notes.note || notes.text || notes.content || "";
  const hasAnything = isMeaningful(generalNotes) ||
    Object.entries(notes).some(([, v]) => isMeaningful(v));

  if (!hasAnything) return <EmptyNote>No notes recorded for this visit.</EmptyNote>;

  return (
    <div className="space-y-2">
      {isMeaningful(generalNotes) && <FieldRow label="Notes" value={generalNotes} />}
      {Object.entries(notes)
        .filter(([key, val]) => !["generalNotes", "notes", "note", "text", "content"].includes(key) && isMeaningful(val))
        .map(([key, val]) => (
          <FieldRow key={key} label={key} value={Array.isArray(val) ? val.join(", ") : String(val)} />
        ))}
    </div>
  );
};

// ─── Chips ────────────────────────────────────────────────────────────

const LabTestChip = ({ test }) => (
  <span className="text-xs px-2 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
    {test.name}
    {test.fileUrl ? (
      <a
        href={test.fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="ml-1 underline"
        onClick={(e) => e.stopPropagation()}
      >
        view
      </a>
    ) : null}
  </span>
);

const DocumentChip = ({ doc, index }) => {
  const href = doc.url || doc.fileUrl;
  const label = doc.name || doc.fileName || `Document ${index + 1}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-xs px-2.5 py-1 rounded-md bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
    >
      {label}
    </a>
  );
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

// ─── Preview line for the collapsed card ─────────────────────────────

const buildPreview = (note) => {
  const diagnosis = splitLoose(note.assessment?.diagnosis);
  if (diagnosis.length) return diagnosis.join(", ");
  if (isMeaningful(note.subjective?.diet) || note.subjective?.vomiting?.present === "Yes") {
    return "Subjective findings recorded";
  }
  return "SOAP note — no diagnosis recorded";
};

// ─── Card ────────────────────────────────────────────────────────────

const BODY_RENDERERS = {
  subjective: (note) => <SubjectiveBody subjective={note.subjective} />,
  objective: (note) => <ObjectiveBody objective={note.objective} />,
  assessment: (note) => <AssessmentBody assessment={note.assessment} />,
  plan: (note) => <PlanBody plan={note.plan} />,
  notes: (note) => <NotesBody notes={note.notes || {}} />,
};

  const SOAPVisitCard = ({ note, defaultOpen = false, statusBadge, orderLabel }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const documents = note.document?.documents || [];
  const summary = note.summary || {};
  const sectionKeys = ["subjective", "objective", "assessment", "plan", "notes"];
  const active = isActiveNote(note);

  const title = summary.reasonForVisit || summary.serviceName || "SOAP summary";
  const metaLine = [summary.serviceName, summary.staffName].filter(isMeaningful).join(" · ");

  return (
    <div
      className="bg-white rounded-xl border shadow-sm mb-4 overflow-hidden"
      style={{
        borderColor: active ? "#16a34a" : "#e5e7eb",
        borderLeftWidth: active ? "4px" : "1px",
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex items-center justify-between gap-3 p-5 text-left"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-gray-900 truncate">{title}</h3>
            {statusBadge}
          </div>
          {orderLabel && (
            <p className="mt-0.5 flex items-center gap-1">
              <span className="font-mono bg-gray-100 text-gray-700 border border-gray-200 px-2 py-0.5 rounded text-[11px] font-semibold tracking-wide">{orderLabel}</span>
            </p>
          )}
          {isMeaningful(metaLine) && <p className="text-xs text-gray-400 mt-0.5 truncate">{metaLine}</p>}
          {!isOpen && <p className="text-sm text-gray-500 mt-0.5 truncate">{buildPreview(note)}</p>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm text-gray-600">
            {isMeaningful(summary.date) ? summary.date : formatDate(note.updatedAt || note.createdAt)}
          </span>
          <ChevronDown size={18} className={`text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </button>

      {isOpen && (
        <div className="px-5 pb-5">
          {isMeaningful(summary.serviceNote) && (
            <p className="text-sm text-gray-600 italic mb-3 pb-3 border-b border-gray-100">{summary.serviceNote}</p>
          )}
          <div className="space-y-0">
            {sectionKeys.map((key, idx) => {
              const meta = SECTION_META[key];
              const isLast = idx === sectionKeys.length - 1;
              return (
                <div key={key} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
                      style={{ backgroundColor: meta.bg, color: meta.fg }}
                    >
                      {meta.label}
                    </span>
                    {!isLast && <div className="w-px flex-1 bg-gray-200 my-1" />}
                  </div>
                  <div className={isLast ? "pb-0 flex-1 min-w-0" : "pb-4 flex-1 min-w-0"}>
                    <p className="text-sm font-medium text-gray-900 mb-1">{meta.title}</p>
                    {BODY_RENDERERS[key](note)}
                  </div>
                </div>
              );
            })}
          </div>

          {documents.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 mb-2">Documents</p>
              <div className="flex flex-wrap gap-2">
                {documents.map((doc, i) => (
                  <DocumentChip key={i} doc={doc} index={i} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Pull every vet-clinic booking ID that belongs to this pet, out of the
// client's order/appointment data (order_details[].items[]). ─────────────

const extractBookingIdsForPet = (orders, petId) => {
  const ids = new Set();
  if (!Array.isArray(orders) || !petId) return [];

  orders.forEach((order) => {
    (order.items || []).forEach((item) => {
      if (item?.type !== "vet_clinic") return;
      const info = item.appointment_info;
      if (!info) return;
      if (info.vendor_pet_id !== petId) return;
      const bookingId = info._id || item.ref_id;
      if (bookingId) ids.add(bookingId);
    });
  });

  return Array.from(ids);
};

const getNoteDate = (note) => note.updatedAt || note.createdAt || note.summary?.date || null;

const isActiveNote = (note) =>
  ["in_progress", "in-progress", "active"].includes(String(note.status || "").toLowerCase());

const PetSOAPSummary = ({ petData, orders = [] }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Build a map from order _id -> human-readable order number (e.g. "ORD-9242")
  const orderMap = useMemo(() => {
    const map = {};
    orders.forEach((order) => {
      const id = order._id || order.id;
      const label = order.orderNo || order.order_no || order.orderNumber || order.order_number || order.orderId;
      if (id && label) map[id] = label;
    });
    return map;
  }, [orders]);

  // const bookingIds = useMemo(
  //   () => extractBookingIdsForPet(orders, petData?._id),
  //   [orders, petData?._id]
  // );
  const petId = petData?._id || petData?.vendor_pet_id;

  useEffect(() => {
    //if (bookingIds.length === 0) {
    if (!petId) {
      setNotes([]);
      return;
    }

    const fetchSOAPHistory = async () => {
      setLoading(true);
      try {
        // const results = await Promise.allSettled(
        //   bookingIds.map((id) => httpService.get(`/vendorpet/getvendorsoap/${id}`))
        // );
        const responses = await Promise.all(
          ["in_progress", "completed"].map((status) =>
            httpService.get(`/vendorpet/getvendorsoapbypet/${petId}?status=${status}`),
          ),
        );

        // const fetchedNotes = results
        //   .filter((r) => r.status === "fulfilled" && r.value?.code === 200)
        //   .map((r) => r.value.data)
        //   .filter(Boolean)
        //   .sort((a, b) => {
        //     const dateA = new Date(a.updatedAt || a.createdAt || 0);
        //     const dateB = new Date(b.updatedAt || b.createdAt || 0);
        //     return dateB - dateA;
        //   });

        // setNotes(fetchedNotes);
        const fetchedNotes = responses.flatMap((response) =>
          Array.isArray(response?.data?.notes) ? response.data.notes : [],
        );
        const uniqueNotes = Array.from(
          new Map(fetchedNotes.map((note) => [note._id, note])).values(),
        );
        uniqueNotes.sort((a, b) => {
          const activeDifference = Number(isActiveNote(b)) - Number(isActiveNote(a));
          if (activeDifference !== 0) return activeDifference;
          return new Date(getNoteDate(b) || 0) - new Date(getNoteDate(a) || 0);
        });
        setNotes(uniqueNotes);
      } catch (err) {
        console.error("Failed to load SOAP history:", err);
      } finally {
        setLoading(false);
      }
    };

  //   fetchSOAPHistory();
  // }, [bookingIds]);
   fetchSOAPHistory();
  }, [petId]);

  const filteredNotes = useMemo(() => {
    const start = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
    const end = toDate ? new Date(`${toDate}T23:59:59.999`) : null;

    return notes.filter((note) => {
      const active = isActiveNote(note);
      const noteDate = new Date(getNoteDate(note) || 0);
      const matchesStatus =
        statusFilter === "all" || (statusFilter === "active" ? active : !active);
      const matchesFrom = !start || noteDate >= start;
      const matchesTo = !end || noteDate <= end;
      return matchesStatus && matchesFrom && matchesTo;
    });
  }, [fromDate, notes, statusFilter, toDate]);

  if (loading) return <LoaderSpiner text="Loading SOAP history..." />;

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          Status
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-md border border-gray-300 bg-white px-2.5 py-2 text-sm font-normal text-gray-700"
          >
            <option value="all">All SOAP notes</option>
            <option value="active">Active only</option>
            <option value="old">Old only</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          From
          <input
            type="date"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            className="rounded-md border border-gray-300 px-2.5 py-2 text-sm font-normal text-gray-700"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          To
          <input
            type="date"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            className="rounded-md border border-gray-300 px-2.5 py-2 text-sm font-normal text-gray-700"
          />
        </label>
        <span className="pb-2 text-xs text-gray-500">
          {filteredNotes.length} note{filteredNotes.length === 1 ? "" : "s"}
        </span>
      </div>

      {filteredNotes.length === 0 && (
        <div className="text-center py-16">
          <p className="text-sm text-gray-400">
            {notes.length === 0 ? "No SOAP notes recorded yet." : "No SOAP notes match these filters."}
          </p>
        </div>
      )}

      {filteredNotes.map((note, idx) => {
        const rawOrderId = note.orderId || note.order_id || note.orderID || note.summary?.orderId || note.summary?.order_id || null;
        const orderLabel = rawOrderId ? (orderMap[rawOrderId] || rawOrderId) : null;
        return (
          <SOAPVisitCard
            key={note._id}
            note={note}
            defaultOpen={idx === 0}
            orderLabel={orderLabel}
            statusBadge={isActiveNote(note) ? (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                Active
              </span>
            ) : (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Old
              </span>
            )}
          />
        );
      })}
    </div>
  );
};

export default PetSOAPSummary;